/* eslint camelcase: 0 */
import { Button, Checkbox, Collapse, RotatingChevron } from "@newsela/angelou";

import {
  actionPrefixes,
  actionNames,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { studentNameComparator } from "@/classrooms/utils/studentNameComparator";
import {
  AssignmentGroup,
  SmallGroupStudent,
} from "@/types/models/Assignment.interface";
import {
  ClassroomStudent,
  Classroom,
} from "@/types/models/Classroom.interface";
import { errorBorderCss } from "../AssignmentOptions.style";
import {
  buttonCss,
  noStudentsCss,
  ulCss,
} from "./SmallGroupAssignmentOptions.style";

const CheckboxWithTrackEvent = withTrackEvent(Checkbox) as typeof Checkbox;
const CollapseWithTrackEvent = withTrackEvent(Collapse);

type checkboxProps = React.ComponentPropsWithoutRef<typeof Checkbox>;
type onClickType = checkboxProps["onClick"];
export interface SmallGroupAssignmentOption {
  allTeacherClassrooms: Classroom[];
  areAllClassroomsVisible: boolean;
  assignment: {
    assignment_groups: AssignmentGroup[];
  };
  hasError: boolean;
  onClick: onClickType;
}

export const SmallGroupAssignmentOptions = ({
  allTeacherClassrooms,
  areAllClassroomsVisible,
  assignment,
  hasError,
  onClick,
}: SmallGroupAssignmentOption) => {
  const INITIAL_VISIBLE_CLASSROOMS = 3;

  const { assignment_groups } = assignment;

  const determineIsChecked = (student: ClassroomStudent) => {
    const [group] = assignment_groups;
    const users = group?.users;

    const assignedStudent: SmallGroupStudent | undefined = users?.find(
      (user) => user.unique_id === student.unique_id,
    );

    return Boolean(assignedStudent);
  };

  const initialVisibleClassrooms =
    allTeacherClassrooms?.length > INITIAL_VISIBLE_CLASSROOMS
      ? allTeacherClassrooms?.slice(0, INITIAL_VISIBLE_CLASSROOMS)
      : allTeacherClassrooms;

  const classroomsToDisplay = areAllClassroomsVisible
    ? allTeacherClassrooms
    : initialVisibleClassrooms;

  const { isHidden, onClick: setIsHidden } = Collapse.useCollapse({
    isHidden: true,
  });

  const eventProperties = {
    actionName: actionNames.add,
    actionPrefix: actionPrefixes.initiate,
    objectType: objectTypes.assignment,
  };

  return (
    <ul css={ulCss}>
      {classroomsToDisplay?.map((classroom) => {
        return (
          <li key={classroom.unique_id}>
            <CollapseWithTrackEvent
              hidden={isHidden} /* @ts-expect-error with buttonProps*/
              buttonProps={{
                dataProps: {
                  "data-qa-selector": `small group classroom: ${classroom.name}`,
                },
                flavor: Button.flavors.polarTertiary,
                __cssFor: {
                  root: [buttonCss, hasError ? errorBorderCss : null],
                },
              }}
              onClick={setIsHidden}
              /* @ts-expect-error with buttonContents*/
              renderButtonContents={(isHidden: boolean, timeout: number) => {
                return (
                  <>
                    {classroom.name}
                    <RotatingChevron isDown={isHidden} timeout={timeout} />
                  </>
                );
              }}
              {...eventProperties}
            >
              {classroom.students?.length === 0 ? (
                <div css={noStudentsCss}>This class doesn't have students.</div>
              ) : (
                <ul css={ulCss}>
                  {classroom.students
                    ?.sort(studentNameComparator)
                    ?.map((student) => {
                      const isChecked = determineIsChecked(student);
                      return (
                        <li key={student.unique_id}>
                          <CheckboxWithTrackEvent
                            checked={isChecked}
                            dataProps={{
                              "data-qa-selector": `student ${student.user.first_name} ${student.user.last_name}`,
                            }}
                            label={`${student.user.first_name} ${student.user.last_name}`}
                            name="student"
                            {...eventProperties}
                            onClick={(e) => (onClick ? onClick(e) : null)}
                            value={student.unique_id}
                            __cssFor={{
                              root: hasError ? errorBorderCss : null,
                            }}
                          />
                        </li>
                      );
                    })}
                </ul>
              )}
            </CollapseWithTrackEvent>
          </li>
        );
      })}
    </ul>
  );
};

export default SmallGroupAssignmentOptions;
